<template>
  <vddl-draggable
    v-touch="onTouch"
    class="board"
    :draggable="item"
    :index="index"
    :disable-if="isdisabled || !canEdit"
    :wrapper="list"
    :dragstart="onDragStart"
    :dragend="onDragEnd"
    :moved="onMove"
    :canceled="onCancel"
    :type="type"
    :class="{ opened: getOpen === item.id, popupon: isPopoverActive }"
  >
    <div
      v-if="getIsRecursiveContainer(item.id)"
      class="folder_container"
      :class="{ collapsed: getIsCollapsed(item.id), empty: !hasChildren }"
      :draggable="!isdisabled && canEdit"
      @dragstart="onBoardStartDrag($event)"
    >
      <div
        class="folder"
        :class="{ selected: isSelected }"
        @contextmenu.prevent="onContextMenu($event)"
      >
        <div class="arrow" @click.stop="toggle(item)">
          <svg-icon :width="'8'" :height="'8'" icon="app-icons/arrow-down" />
        </div>
        <div class="icon">
          <svg-icon
            ref="icon"
            :width="'16'"
            :height="'16'"
            icon="app-icons/folder"
            @on-load="onIconLoad"
          />
        </div>
        <input
          v-if="isEditing"
          ref="nameInput"
          class="nameinput"
          @blur="onBlurText($event, item)"
          @keyup.enter="onBlurText($event, item)"
        />
        <span
          v-else
          type="text"
          class="name"
          @dblclick="onDblclickText($event, item)"
        >
          {{ board.name }}
        </span>
        <VDropdown
          v-if="canEdit"
          popper-class="v-popper--boardListItem-dropdown"
          placement="bottom-start"
        >
          <button type="button" class="btn-add">
            <svg-icon :width="'11'" :height="'11'" icon="app-icons/create" />
          </button>
          <template #popper>
            <div class="menu">
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateBoard"
              >
                Create Board
              </button>
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateFolder"
              >
                Create Folder
              </button>
            </div>
          </template>
        </VDropdown>
      </div>
      <!-- <transition name="slide"> -->
      <vddl-list
        class="panel__body"
        :list="containedBoards"
        :drop="onDrop"
        :disable-if="isdisabled || !canEdit"
        :external-sources="true"
        :allowed-types="allowedTypes"
      >
        <list
          v-for="(col, number) in containedBoards"
          :key="col.id"
          :item="col"
          :list="containedBoards"
          :index="number"
          :disable-if="isdisabled || !canEdit"
          :container-id="item.id"
        />
      </vddl-list>
    </div>
    <div
      v-else
      class="item_container"
      :class="{
        empty: !hasElementChildren,
      }"
    >
      <div
        class="item"
        :class="{
          collapsed: getIsCollapsed(item.id),
          selected: isSelected,
        }"
        :draggable="!isdisabled && canEdit"
        @contextmenu.prevent="onContextMenu($event)"
        @dragstart="onBoardStartDrag($event)"
      >
        <div class="arrow" @click.stop="toggle(item)">
          <svg-icon :width="'8'" :height="'8'" icon="app-icons/arrow-down" />
        </div>
        <div class="thumb" @click="onClick">
          <svg-icon
            ref="icon"
            fill="#BCBCBC"
            icon="app-icons/board"
            :width="'18'"
            :height="'18'"
            @on-load="onIconLoad"
          />
        </div>
        <div class="nameholder" @click="onClick">
          <input
            v-if="isEditing"
            ref="nameInput"
            class="nameinput"
            spellcheck="false"
            @blur="onBlurText($event, item)"
            @keyup.enter="onBlurText($event, item)"
          />
          <span
            v-else
            type="text"
            class="name"
            @dblclick="onDblclickText($event, item)"
          >
            {{ board.name }}
          </span>
        </div>
        <VDropdown
          v-if="canEdit"
          popper-class="v-popper--boardListItem-dropdown"
          placement="bottom-start"
        >
          <button type="button" class="btn-add">
            <svg-icon :width="'11'" :height="'11'" icon="app-icons/create" />
          </button>
          <template #popper>
            <div class="menu">
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateBoard"
              >
                Create Board
              </button>
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateFolder"
              >
                Create Folder
              </button>
            </div>
          </template>
        </VDropdown>
      </div>
      <div v-if="!isCollapsed" class="list">
        <vddl-list
          class="panel__body"
          :list="containedElements"
          :drop="onDropElement"
          :disable-if="isdisabled || !canEdit || otherList"
          :external-sources="true"
          :allowed-types="allowedTypesElements"
        >
          <board-list-element
            v-for="(col, number) in containedElements"
            :key="col.id"
            v-model:title="col.title"
            v-model:content="col.content"
            :item="col"
            :list="containedElements"
            :index="number"
            :container-id="item.id"
          />
        </vddl-list>
      </div>
    </div>
  </vddl-draggable>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import SvgIcon from '../all/SvgIcon.vue';
import BoardListElement from './BoardListElement.vue';
import boardTypes from '../../vuex/project/types/boards-types.js';
import { fittingString } from '../../canvas-utils.js';
import { loadImage } from '../../utils.js';
import updater from '../../vuex/project/updater.js';

export default {
  name: 'List',
  components: {
    SvgIcon,
    'board-list-element': BoardListElement,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    list: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    const { hasPermission: canComment } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_COMMENT],
      roles: ['editor', 'viewer'],
    });

    return {
      canEdit,
      canComment,
    };
  },

  data() {
    return {
      dragCanvas: null,
      isdisabled: false,
      isEditing: false,
      isSelected: false,
      allowedTypes: [DRAG_TYPE.BOARD, DRAG_TYPE.BOARD_CONTAINER],
      allowedTypesElements: [DRAG_TYPE.BOARD_ELEMENT],
      isPopoverActive: false,
      options: {
        placement: 'bottom-start',
        trigger: 'click',
        boundariesElement: document.body,
        delay: {
          show: 100,
          hide: 100,
        },
        popoverBaseClass: 'tooltip popover create_popup',
        popoverInnerClass: 'tooltip-inner popover-inner',
        popoverArrowClass: 'tooltip-arrow popover-arrow',
        offset: '0',
      },
    };
  },

  computed: {
    ...mapGetters('project/data/boards', [
      'getIsRecursiveContainer',
      'getContainedNodes',
      'getContainerNode',
      'getContainerNodes',
      'getIndex',
      'getNode',
      'getRootContainer',
      'getNonContainerBoards',
    ]),
    ...mapGetters('project/config/boards', [
      'getSelections',
      'getIsCollapsed',
      'getOpen',
    ]),
    ...mapGetters('application/drag', [
      'getItems',
      'getSource',
      'getTarget',
      'getIsEmpty',
    ]),
    ...mapGetters('project/data/elements', {
      getElements: 'getNodes',
    }),

    type() {
      return this.getIsRecursiveContainer(this.item.id)
        ? DRAG_TYPE.BOARD_CONTAINER
        : DRAG_TYPE.BOARD;
    },

    board() {
      return this.getNode(this.item.id);
    },

    containedBoards() {
      return this.getContainedNodes(this.item.id, 'boards', 'children');
    },

    containedElements() {
      return this.getElements(this.item.elements);
    },

    hasChildren() {
      return this.containedBoards.length > 0;
    },

    hasElementChildren() {
      return this.containedElements.length > 0;
    },

    isCollapsed() {
      return this.getIsCollapsed(this.item.id);
    },

    otherList() {
      return this.item.id !== this.getSource.ids[0];
    },
  },

  watch: {
    /**
     * Watch whether item is selected.
     */
    getOpen(newValue, oldValue) {
      if (this.item.id === newValue) {
        // get all container ancestor ids
        const containerIds = this.getContainerNodes(
          newValue,
          'boards',
          'children'
        );
        // Expand all container ancestors
        containerIds.forEach(id => {
          this.setCollapsed({
            id,
            value: false,
            apply: false,
          });
        });
        updater.complete({ apply: true });

        // Scroll to item position
        this.$nextTick(() => {
          this.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        });
      }
    },
  },

  created() {
    emitter.on(EVENT.DISABLE_LIST_ITEM, (el, event) => {
      this.isdisabled = true;
    });
    emitter.on(EVENT.ENABLE_LIST_ITEM, (el, event) => {
      this.isdisabled = false;
    });
  },

  methods: {
    ...mapActions('project/data/boards', [
      'customCreate',
      'customUpdate',
      'customDelete',
      'move',
      'duplicate',
    ]),
    ...mapActions('project/config/boards', [
      'setOpen',
      'setCollapsed',
      'select',
    ]),
    ...mapActions('project/data/elements', ['setBoard']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('application/drag', [
      'setItems',
      'setSource',
      'setTarget',
      'clear',
    ]),
    ...mapActions('application/modals', ['setBoardPropertiesModal']),

    /**
     * Custom event from svg icon load
     */
    async onIconLoad(svg) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.assetImage = await loadImage(url);
    },

    /**
     * Expand or collapse a container
     */
    toggle(item) {
      // Do not do anything if in editing mode
      if (this.isEditing) return;
      // Toggle the collapsed property of the container
      this.setCollapsed({
        id: item.id,
        value: !this.isCollapsed,
        apply: true,
      });
    },

    /**
     * Event handler for contextmenu event.
     */
    onContextMenu(event) {
      if (this.canEdit || this.canComment) {
        this.isSelected = true;
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Event handler for click event
     */
    onClick(event) {
      // Do not do anything if in name editing mode
      if (this.isEditing) return;
      // Open this board
      this.setOpen({
        id: this.item.id,
        apply: true,
      });
    },

    onClickCreateBoard() {
      this.createBoard();
    },

    createBoard() {
      const itemId =
        this.item === undefined ? this.getRootContainer.id : this.item.id;
      // Get default container
      let container = this.getContainerNode(itemId, 'boards', 'children');
      // Get default tIndex
      let tIndex = this.getIndex(itemId, 'boards', 'children');
      if (this.getIsRecursiveContainer(itemId)) {
        // Get Current item instead of container ( current item as container and tIndex 0 )
        container = this.getNode(itemId);
        tIndex = 0;
      }
      this.customCreate({
        board: boardTypes.newItem,
        tId: container.id,
        tIndex,
        tExpand: true,
        apply: true,
      });
    },

    onClickCreateFolder() {
      this.createFolder();
    },

    createFolder() {
      const itemId =
        this.item === undefined ? this.getRootContainer.id : this.item.id;
      // Get default container
      let container = this.getContainerNode(itemId, 'boards', 'children');
      // Get default tIndex
      let tIndex = this.getIndex(itemId, 'boards', 'children');
      if (this.getIsRecursiveContainer(itemId)) {
        // Get Current item instead of container ( current item as container and tIndex 0 )
        container = this.getNode(itemId);
        tIndex = 0;
      }
      this.customCreate({
        board: boardTypes.newContainer,
        tId: container.id,
        tIndex,
        tExpand: true,
        apply: true,
      });
    },

    /**
     * Event handler for double click event on text.
     */
    onDblclickText($event, item) {
      if (this.canEdit) {
        this.setRenameMode();
      }
    },

    /**
     * Event handler for blur event on text.
     */
    onBlurText($event, item) {
      const name = $event.target.value;
      this.customUpdate({
        id: item.id,
        props: { name: name || 'Untitled board' },
        apply: true,
      });
      // Destroy input
      this.isEditing = false;
      // Enable dragndrop
      emitter.emit(EVENT.ENABLE_LIST_ITEM);
    },

    /**
     * Event handler for vddl dragstart event. The board item has started dragging.
     */
    onDragStart(draggable) {
      console.log('*****ONDRAGSGTART BOARD', draggable);
      // Add class to body for hover fix
      document.body.classList.add('dragging');

      this.setItems({
        value: {
          type: this.type,
          ids: [this.item.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.BOARDS,
          ids: [this.containerId],
          indexes: [this.index],
        },
      });
    },

    /**
     * Event handler for vddl dragend event.
     */
    onDragEnd(event) {
      console.log('*****DRAG END', event);
      // Add class to body for hover fixasd
      document.body.classList.remove('dragging');

      // Remove any drag canvas
      $('.dragCanvasSidebar').remove();

      console.log($('.dragCanvasSidebar'));
      console.log($('canvas'));
    },

    onTouch(event) {
      if (event.type === 'singletap') {
        console.log('*******ITEM TAP', event);
      } else if (event.type === 'doubletap') {
        console.log('*******ΙΤΕΜ DOUBLE TAP', event);
      } else if (event.type === 'press') {
        console.log('*******ΙΤΕΜ PRESS', event);
        event.preventDefault();
      }
      event.stopPropagation();
    },

    onBoardStartDrag($event) {
      console.log('*****START DRAG', $event);
      if (this.canEdit) {
        const canvas = document.createElement('canvas');
        canvas.classList.add('dragCanvasSidebar');
        canvas.width = 200;
        canvas.height = 32;

        // This is required in Chrome (check styles below)
        document.body.append(canvas);
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        console.log('Icon:', this.$refs.icon.icon);
        if (this.$refs.icon.icon === 'app-icons/folder') {
          ctx.drawImage(this.assetImage, 12, 6, 16, 16);
        } else {
          ctx.drawImage(this.assetImage, 14, 6, 18, 18);
        }

        // Draw text
        ctx.font = '12px "Open Sans",sans-serif';
        ctx.fillStyle = 'white';
        ctx.fillText(fittingString(ctx, this.item.name, 150), 40, 20);

        $event.dataTransfer.setDragImage(
          canvas,
          canvas.width / 2,
          canvas.height / 2
        );
      }
    },

    /**
     * Event handler for vddl drop event. This is trggered before vddl moved event.
     */
    onDrop(draggable) {
      this.setTarget({
        value: {
          type: DRAG_LIST.BOARDS,
          id: this.item === undefined ? this.getRootContainer.id : this.item.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for vddl drop event. This is trggered before vddl moved event.
     */
    onDropElement(draggable) {
      this.setTarget({
        value: {
          type: DRAG_LIST.BOARD,
          id: this.item.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for vddl moved event. The item has been moved to a correct position.
     */
    onMove(event) {
      console.log('*******ONMOVED', event);
      const items = this.getItems;
      const source = this.getSource;
      const target = this.getTarget;
      // In case it was dropped on the boards tree
      if (target.type === DRAG_LIST.BOARDS) {
        this.move({
          id: items.ids[0],
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else if (target.type === DRAG_LIST.ELEMENTS) {
        this.setBoard({
          id: target.id,
          boardId: items.ids[0],
          apply: true,
        });
      }
      // Clear drag information
      this.clear();
    },

    /**
     * Event handler for vddl canceled event.
     */
    onCancel(event) {
      this.clear();
    },

    /**
     * Sets this item in renaming mode
     */
    setRenameMode() {
      // Create input
      this.isEditing = true;

      // Disable dragndrop
      emitter.emit(EVENT.DISABLE_LIST_ITEM);

      // Because of v-if on isEditing the DOM is not rendered yet
      this.$nextTick(() => {
        const { nameInput } = this.$refs;
        nameInput.value = this.board.name;
        nameInput.focus();
        // Set the selection range (instead of select()) to cover Safari also
        nameInput.setSelectionRange(0, nameInput.value.length);
      });
    },

    /**
     * Create a context menu for this board
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      if (this.canEdit) {
        this.createItem({
          name: 'Create Board',
          icon: null,
          commands: {
            doAction: () => {
              const itemId =
                this.item === undefined
                  ? this.getRootContainer.id
                  : this.item.id;
              // Get default container
              let container = this.getContainerNode(
                itemId,
                'boards',
                'children'
              );
              // Get default tIndex
              let tIndex = this.getIndex(itemId, 'boards', 'children');
              if (this.getIsRecursiveContainer(itemId)) {
                // Get Current item instead of container ( current item as container and tIndex 0 )
                container = this.getNode(itemId);
                tIndex = 0;
              }
              this.customCreate({
                board: boardTypes.newItem,
                tId: container.id,
                tIndex,
                tExpand: true,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Create Folder',
          icon: null,
          commands: {
            doAction: () => {
              this.createFolder();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      if (this.canEdit && !this.getIsRecursiveContainer(this.item.id)) {
        this.createItem({
          name: 'Duplicate',
          icon: null,
          commands: {
            doAction: () => {
              this.duplicate({ id: this.item.id, apply: true });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      this.createItem({
        name: 'Properties...',
        icon: null,
        commands: {
          doAction: () => {
            this.openBoardProperties();
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      if (this.canEdit) {
        this.createItem({
          name: 'Rename',
          icon: null,
          commands: {
            doAction: () => {
              this.setRenameMode();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Rename',
          icon: null,
          commands: {
            doAction: () => {
              this.setRenameMode();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Delete',
          icon: null,
          commands: {
            doAction: () => {
              this.customDelete({
                id: this.item.id,
                setOpen: true,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              const nonContainerBoards = this.getNonContainerBoards;
              const isContainer = this.getIsRecursiveContainer(this.item.id);
              return nonContainerBoards.length > 1 || isContainer;
            },
          },
          submenu: null,
        });
      }

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          this.isSelected = false;
        },
      });
    },

    openBoardProperties() {
      this.setBoardPropertiesModal({ value: this.item.id });
    },
  },
};
</script>

<style scoped lang="scss">
@import './SidebarItem.scss';

.sidebar .folder .arrow,
.sidebar .item .arrow {
  width: 15px;
  height: 30px;
  min-width: 15px;
  min-height: 30px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s;
}

.sidebar .board .collapsed .arrow {
  transform: rotate(-90deg);
}

.sidebar .item,
.sidebar .folder {
  display: flex;
  align-items: center;
  justify-content: left;
  transition: background 140ms linear;
  background: transparent;
  //padding:8px 0;
  min-height: 30px;
  width: 100%;
  position: relative;
  padding: 0 10px 0 0;
  cursor: pointer;
}

.sidebar .board {
  // padding-left: 10px;

  .item .icon {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    // margin: 0 10px 0 0;
    color: #fff;
    opacity: 0.2;
    text-align: center;
    line-height: 22px;
    margin: 0 6px 0 0;
  }
}

//.sidebar .board .vddl-draggable {
// padding-left: 22px;
//}

.sidebar .board .item .icon:before {
  content: '\f111';
}

// .sidebar .vddl-draggable.opened .item .icon:before {
//     content: "\f111";
// }

.vddl-draggable,
.vddl-list {
  position: relative;
}

.sidebar .item .thumb {
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  overflow: hidden;
  border-radius: 999px;
  margin: 0 8px 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sidebar .board-list .nameholder {
  flex: 1 1 50%;
  box-sizing: border-box;
  padding: 0 20px 0 0;
  // max-width: calc(100% - 10px);
  font-size: 12px;
  overflow: hidden;
}

.sidebar .board-list .name {
  display: block;
  color: #bcbcbc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  text-align: left;
  font-size: 12px;
  user-select: none;
}

.sidebar .board-list .nameinput {
  font-family: 'Open Sans', sans-serif;
  color: #db841e;
  overflow: hidden;
  width: 100%;
  background: #0d0e0e;
  border: none;
  cursor: text;
  text-align: left;
  font-size: 12px;
  -webkit-touch-callout: auto;
  -webkit-user-select: auto;
  -khtml-user-select: auto;
  -moz-user-select: auto;
  -ms-user-select: auto;
  user-select: auto;
}

.btn-add {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  width: 26px;
  height: 26px;
  cursor: pointer;
  border-radius: 4px;

  &[data-popper-shown] {
    background: #2c3132;
  }
}
</style>
<style lang="scss">
.v-popper__popper.v-popper--theme-dropdown.v-popper--boardListItem-dropdown {
  .v-popper__inner {
    background: #0d0e0e;
    border: 1px solid #414c4d;
    border-radius: 3px;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    padding: 0;
    color: #bcbcbc;
    margin-top: -7px;
  }
  .v-popper__arrow-container {
    display: none;
  }

  .menu {
    .option {
      align-items: center;
      cursor: pointer;
      display: flex;
      padding: 10px 16px;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      border: none;
      color: #bcbcbc;
      width: 100%;
      font-size: 13px;

      &:hover {
        background: #363e3f;
      }
    }
  }
}
</style>
